import { useRef } from "react";

import { ReadField, readOption } from "./ReadOnly";
import "./editors.css";

/**
 * A single-select segmented strip — the device strip's shape, for a choice
 * where exactly one option holds.
 *
 * Same reasoning as DevicePicker: a handful of options, all short enough to
 * read at a glance, so a dropdown cost a click just to learn what the choice
 * was. Buttons rather than Fluent's ChoiceGroup because a segmented strip is
 * the one control the library has no equivalent of.
 *
 * Wired as a real radiogroup — one tab stop for the strip, arrows to move
 * between segments — which is what a mutually exclusive choice owes the
 * keyboard. DevicePicker's `aria-pressed` toggles would announce "pressed"
 * here instead of "2 of 4", and nothing would say the modes exclude each other.
 */
export default function SegmentedField({
  label,
  labelId,
  options,
  value,
  onChange,
  disabled = false,
}) {
  const refs = useRef([]);
  const index = options.findIndex((option) => option.value === value);

  /* Read mode is the chosen option, named. A strip of four buttons with one of
     them faintly on is a control asking to be used; the mode it is in is all
     there is to say about it. */
  if (disabled) {
    return <ReadField label={label} value={readOption(options, value)} />;
  }

  /** Arrows move the selection itself, the way native radios do. */
  const onKeyDown = (event) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step || disabled) return;
    event.preventDefault();
    const next = (Math.max(index, 0) + step + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      className="hpv2-segmented"
      role="radiogroup"
      aria-label={labelId ? undefined : label}
      aria-labelledby={labelId}
      onKeyDown={onKeyDown}
    >
      {options.map((option, i) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[i] = node;
            }}
            type="button"
            role="radio"
            className="hpv2-segment"
            aria-checked={selected}
            /* One tab stop for the whole strip; the first segment stands in
               when nothing is selected, so the strip is never unreachable. */
            tabIndex={selected || (index === -1 && i === 0) ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
